import React from "react";
import {RouteComponentProps} from "react-router-dom";
import {BrandAdminServiceSingleton} from "src/Context";
import {AdminBrand, BrandRequest} from "../model/BrandTypes";
import {BrandPreview} from "./BrandPreview";
import {BrandTheme} from "src/modules/brand/BrandConfig";
import {resolveLogoUrl} from "src/modules/brand/readBrandConfig";
import "../Branding.scss";

interface Params { id?: string; }

interface State {
    form: BrandRequest;
    brand: AdminBrand | null;
    /** Derived from the color as it is typed, so the form is useful before the first save. */
    livePreviewTheme: BrandTheme | null;
    originalSlug: string | null;
    slugTouched: boolean;
    slugAvailable: boolean | null;
    uploadEnabled: boolean;
    /**
     * A logo chosen while creating a brand, held until there is an id to attach it to. The upload
     * endpoint is POST /brands/{id}/logo, so on a new brand the file waits for the save.
     */
    pendingLogo: File | null;
    pendingLogoPreview: string | null;
    saving: boolean;
    error: string | null;
    notice: string | null;
}

const EMPTY: BrandRequest = {
    slug: "", displayName: "", relationshipLine: "", legalEntity: "", nmls: "",
    primaryColor: "#0F5561", surfaceColor: "", onSurfaceColor: "", linkColor: "", tintColor: "",
    logoUrl: "", termsUrl: "", licensingUrl: "", privacyUrl: "",
    statementDescriptor: ""
};

export class BrandFormPage extends React.Component<RouteComponentProps<Params>, State> {

    state: State = {
        form: {...EMPTY}, brand: null, livePreviewTheme: null, originalSlug: null,
        slugTouched: false, slugAvailable: null,
        uploadEnabled: false, pendingLogo: null, pendingLogoPreview: null,
        saving: false, error: null, notice: null
    };

    private themeRequest = 0;
    private errorRef = React.createRef<HTMLDivElement>();

    private get id(): number | undefined {
        const raw = this.props.match.params.id;
        return raw && raw !== "new" ? Number(raw) : undefined;
    }

    componentDidMount() {
        BrandAdminServiceSingleton.logoUploadEnabled()
            .then(r => this.setState({uploadEnabled: r.enabled}))
            .catch(() => this.setState({uploadEnabled: false}));

        if (this.id) {
            BrandAdminServiceSingleton.get(this.id)
                .then(brand => this.setState({brand, slugTouched: true, originalSlug: brand.slug, form: {
                    slug: brand.slug, displayName: brand.displayName,
                    relationshipLine: brand.relationshipLine || "", legalEntity: brand.legalEntity || "",
                    nmls: brand.nmls || "", primaryColor: brand.primaryColor,
                    surfaceColor: brand.surfaceColor || "",
                    onSurfaceColor: brand.onSurfaceColor || "",
                    linkColor: brand.linkColor || "",
                    tintColor: brand.tintColor || "",
                    logoUrl: brand.logoUrl || "",
                    termsUrl: brand.termsUrl || "", licensingUrl: brand.licensingUrl || "",
                    privacyUrl: brand.privacyUrl || "",
                    statementDescriptor: brand.statementDescriptor || ""
                }}, this.refreshTheme))
                .catch(e => this.setState({error: this.message(e)}));
        } else {
            this.refreshTheme();
        }
    }

    /**
     * Asks the server to derive a theme for the color currently typed.
     *
     * Server-side on purpose: reimplementing the contrast rules in the browser is exactly how the
     * preview and the live page would drift apart. Responses are sequenced so a slow reply for an
     * earlier color cannot overwrite a newer one.
     */
    private refreshTheme = () => {
        const color = (this.state.form.primaryColor || "").trim();
        if (!/^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(color)) {
            this.setState({livePreviewTheme: null});
            return;
        }
        const ticket = ++this.themeRequest;
        BrandAdminServiceSingleton.previewTheme(color, this.state.form)
            .then(theme => {
                if (ticket === this.themeRequest) this.setState({livePreviewTheme: theme});
            })
            .catch(() => {
                if (ticket === this.themeRequest) this.setState({livePreviewTheme: null});
            });
    };

    private message = (e: any) => (e && e.message) ? e.message : "Something went wrong";

    private set = (key: keyof BrandRequest) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        this.setState(prev => {
            const form = {...prev.form, [key]: value};
            // Keep the slug tracking the name until the admin edits it themselves.
            if (key === "displayName" && !prev.slugTouched) {
                form.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
            }
            return {form} as State;
        }, () => {
            if (key === "slug" || (key === "displayName" && !this.state.slugTouched)) this.checkSlug();
            if (["primaryColor", "surfaceColor", "onSurfaceColor", "linkColor", "tintColor"]
                .indexOf(key as string) > -1) this.refreshTheme();
        });
    };

    private onSlugEdited = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({slugTouched: true});
        this.set("slug")(event);
    };

    private checkSlug = () => {
        const slug = this.state.form.slug;
        if (!slug) { this.setState({slugAvailable: null}); return; }
        BrandAdminServiceSingleton.slugAvailable(slug, this.id)
            .then(r => this.setState({slugAvailable: r.available}))
            .catch(() => this.setState({slugAvailable: null}));
    };

    private save = () => {
        this.setState({saving: true, error: null, notice: null});
        const request = this.id
            ? BrandAdminServiceSingleton.update(this.id, this.state.form)
            : BrandAdminServiceSingleton.create(this.state.form);
        request
            .then(this.attachPendingLogo)
            .then(brand => this.setState({brand, saving: false, notice: "Saved."},
                () => this.props.history.push("/internal/branding")))
            .catch(e => this.setState({error: this.message(e), saving: false}));
    };

    /**
     * Uploads a logo chosen before the brand existed, now that it has an id.
     * <p>
     * A failure here is reported but does not fail the save: the brand has already been created at
     * this point, and pretending otherwise would leave the admin retrying a create that would then
     * collide on the slug. They land on the list with the brand present and no logo, which is a
     * state they can fix by editing it.
     */
    private attachPendingLogo = (brand: AdminBrand): Promise<AdminBrand> => {
        const file = this.state.pendingLogo;
        if (!file || !brand || !brand.id) return Promise.resolve(brand);
        return BrandAdminServiceSingleton.uploadLogo(brand.id, file)
            .then(withLogo => {
                this.clearPendingLogo();
                return withLogo;
            })
            .catch(e => {
                this.setState({
                    error: `"${brand.displayName}" was created, but its logo could not be `
                        + `uploaded: ${this.message(e)}. Open the brand to try again.`
                });
                return brand;
            });
    };

    private clearPendingLogo = () => {
        const url = this.state.pendingLogoPreview;
        if (url) URL.revokeObjectURL(url);
        this.setState({pendingLogo: null, pendingLogoPreview: null});
    };

    private upload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        // Creating: there is no id to POST to yet, so keep the file and show it from a local
        // object URL. The save attaches it.
        if (!this.id) {
            if (this.state.pendingLogoPreview) URL.revokeObjectURL(this.state.pendingLogoPreview);
            this.setState({
                pendingLogo: file,
                pendingLogoPreview: URL.createObjectURL(file),
                error: null
            });
            return;
        }

        this.setState({error: null});
        BrandAdminServiceSingleton.uploadLogo(this.id, file)
            .then(brand => this.setState(prev => ({
                brand,
                // Mirror the stored URL into the form so a later save does not overwrite it.
                form: {...prev.form, logoUrl: brand.logoUrl || ""},
                notice: "Logo updated."
            })))
            .catch(e => this.setState({error: this.message(e)}));
    };

    private useSuggested = () => {
        const theme = this.state.livePreviewTheme || (this.state.brand ? this.state.brand.theme : null);
        const suggested = theme && theme.suggestedSurface;
        if (suggested) {
            this.setState(prev => ({form: {...prev.form, primaryColor: suggested}}), this.refreshTheme);
        }
    };

    render() {
        const {form, brand, livePreviewTheme, originalSlug, uploadEnabled, saving, error, notice,
            pendingLogo, pendingLogoPreview} = this.state;
        // A logo not yet uploaded previews from its local object URL; a stored one from its URL.
        const logoPreviewSrc = pendingLogoPreview || (form.logoUrl ? resolveLogoUrl(form.logoUrl) : "");
        // The live derivation wins so the form is useful before the first save.
        const theme = livePreviewTheme || (brand ? brand.theme : null);
        const slugChanged = !!originalSlug && !!form.slug && form.slug !== originalSlug;

        return (
            <div className="branding">
                <div className="branding__bar">
                    <div>
                        <h2 className="branding__title">{this.id ? "Edit brand" : "New brand"}</h2>
                        <p className="branding__subtitle">
                            The simpl. hero and the &ldquo;Powered by Network Funding, LP.&rdquo;
                            home block are unaffected by anything here.
                        </p>
                    </div>
                    <button className="branding__btn"
                            onClick={() => this.props.history.push("/internal/branding")}>
                        BACK
                    </button>
                </div>

                {error
                    ? <div className="branding__error" ref={this.errorRef} tabIndex={-1} role="alert">{error}</div>
                    : null}
                {notice ? <div className="branding__notice">{notice}</div> : null}

                <div className="branding__cols">
                    <div className="branding__col">
                        <section className="branding__card">
                            <h3>Lender identity</h3>
                            <div className="branding__row">
                                {this.field("Brand name *", "displayName", form.displayName,
                                    "Shown in the lender row on the tile.")}
                                {this.slugField()}
                            </div>
                            <div className="branding__row">
                                {this.field("Relationship wording", "relationshipLine", form.relationshipLine || "",
                                    'e.g. "Triumph Lending is a DBA of Network Funding"')}
                                {this.field("Legal entity", "legalEntity", form.legalEntity || "",
                                    "Named in the card-authorization agreement.")}
                            </div>
                            <div className="branding__row">
                                {this.field("NMLS ID", "nmls", form.nmls || "",
                                    "Usually 2297 for a Network Funding brand, but editable.")}
                                {this.field("Bank statement name", "statementDescriptor",
                                    form.statementDescriptor || "",
                                    "What borrowers see on their card statement. Leave blank to use " +
                                    "the Stripe account's own. Max 22 characters; < > \\ \" ' and * " +
                                    "are not allowed.")}
                            </div>
                            {slugChanged
                                ? <div className="branding__warn">
                                    Changing the slug from <code>{originalSlug}</code> to{" "}
                                    <code>{form.slug}</code> breaks any payment link already sent with
                                    the old one — those borrowers will see the default brand instead.
                                  </div>
                                : null}
                        </section>

                        <section className="branding__card">
                            <h3>Links</h3>
                            <p className="branding__help">
                                Pasted links &mdash; nothing is uploaded and the design does not change,
                                only the destination.
                            </p>
                            <div className="branding__row">
                                {this.field("Terms URL", "termsUrl", form.termsUrl || "", "")}
                                {this.field("Licensing URL", "licensingUrl", form.licensingUrl || "", "")}
                            </div>
                            <div className="branding__row">
                                {this.field("Privacy URL", "privacyUrl", form.privacyUrl || "", "")}
                            </div>
                        </section>

                        <section className="branding__card">
                            <h3>Brand color</h3>
                            <p className="branding__help">
                                Paste the lender&rsquo;s published hex. Surface, link and text colors are
                                derived &mdash; no second color to pick.
                            </p>
                            <div className="branding__colorRow">
                                <span className="branding__swatch" style={{background: form.primaryColor}}/>
                                <label className="branding__field branding__field--narrow">
                                    <span>Published color (hex) *</span>
                                    <input value={form.primaryColor} onChange={this.set("primaryColor")}/>
                                </label>
                                <input type="color" className="branding__colorPicker"
                                       value={/^#[0-9a-fA-F]{6}$/.test(form.primaryColor) ? form.primaryColor : "#000000"}
                                       onChange={this.set("primaryColor")}/>
                            </div>

                            {theme ? this.derived(theme) : (
                                <p className="branding__help">Enter a hex color to see the derived values.</p>
                            )}
                        </section>

                        <section className="branding__card">
                            <h3>Logo</h3>
                            <p className="branding__help">
                                One horizontal lockup, shown on a white chip above the home block &mdash; so a
                                full-color logo stays legible on any brand surface.
                            </p>
                            {logoPreviewSrc
                                ? <div className="branding__logoPreview"
                                       style={{background: theme ? theme.surface : form.primaryColor}}>
                                    <span className="branding__logoChip">
                                        <img src={logoPreviewSrc} alt={form.displayName || "Logo"}/>
                                    </span>
                                  </div>
                                : null}

                            {uploadEnabled
                                ? <div className="branding__uploadRow">
                                    <input type="file"
                                           accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                           onChange={this.upload}/>
                                    {logoPreviewSrc
                                        ? <button className="branding__link" onClick={this.removeLogo}>
                                            Remove logo
                                          </button>
                                        : null}
                                  </div>
                                : null}
                            {!uploadEnabled
                                ? <p className="branding__help">
                                    Logo upload is not set up in this environment, so a logo cannot be
                                    added here yet.
                                  </p>
                                : null}
                            {pendingLogo
                                ? <p className="branding__help">
                                    Ready to upload &mdash; it is attached when you save the brand.
                                  </p>
                                : null}
                        </section>

                        <div className="branding__actions">
                            <button className="branding__btn branding__btn--primary"
                                    disabled={saving || !form.displayName || !form.primaryColor}
                                    onClick={this.save}>
                                {saving ? "SAVING…" : "SAVE BRANDING"}
                            </button>
                        </div>
                    </div>

                    <div className="branding__side">
                        <BrandPreview form={form} theme={theme} logoUrl={brand ? brand.logoUrl : ""}/>
                    </div>
                </div>
            </div>
        );
    }

    private field(label: string, key: keyof BrandRequest, value: string, help: string) {
        return (
            <label className="branding__field">
                <span>{label}</span>
                <input value={value} onChange={this.set(key)}/>
                {help ? <em>{help}</em> : null}
            </label>
        );
    }

    private slugField() {
        const {form, slugAvailable} = this.state;
        return (
            <label className="branding__field">
                <span>Slug *</span>
                <input value={form.slug || ""} onChange={this.onSlugEdited} onBlur={this.checkSlug}/>
                <em>
                    The payment link: <code>{window.location.host}/{form.slug || "…"}</code>
                    {slugAvailable === false
                        ? <strong className="branding__taken"> — already in use or reserved</strong>
                        : null}
                    {slugAvailable === true ? <strong className="branding__free"> — available</strong> : null}
                </em>
            </label>
        );
    }

    private derived(theme: any) {
        const {form} = this.state;
        return (
            <>
                <p className="branding__help" style={{marginTop: 18}}>
                    Each color below is worked out from the published color. Edit any of them to set
                    it yourself; clear the box to go back to the calculated value.
                </p>
                <div className="branding__swatches">
                    {this.colorRow("Tile surface", "surfaceColor", form.surfaceColor || "",
                        theme.surface, theme.derived.surface,
                        `${theme.rawContrastOnWhite}:1 against white`)}
                    {this.colorRow("Text on tile", "onSurfaceColor", form.onSurfaceColor || "",
                        theme.onSurface, theme.derived.onSurface,
                        `${theme.onSurfaceContrast}:1 against the tile`)}
                    {this.colorRow("Link on white", "linkColor", form.linkColor || "",
                        theme.linkOnWhite, theme.derived.linkOnWhite,
                        theme.linkAdjusted ? "darkened to stay readable" : "as published")}
                    {this.colorRow("Tint", "tintColor", form.tintColor || "",
                        theme.tint, theme.derived.tint, "8% on white")}
                </div>
                {!theme.bodyTextOnSurfacePasses
                    ? <div className="branding__warn">
                        Body text on the tile scores {theme.onSurfaceContrast}:1, below the 4.5:1
                        needed for small text. The heading is large enough to pass either way.
                        {theme.suggestedSurface
                            ? <> A deeper <code>{theme.suggestedSurface}</code> would clear it.
                                <button className="branding__link" onClick={this.useSuggested}>Use it</button>
                              </>
                            : null}
                      </div>
                    : null}
            </>
        );
    }

    /**
     * One editable color. Shows the value in use, and whether it is calculated or hand-set — an
     * empty box means "calculated", which is why clearing it is how you reset.
     */
    private colorRow(label: string, key: keyof BrandRequest, value: string,
                     inUse: string, calculated: string, note: string) {
        const overridden = !!value;
        return (
            <div className="branding__colorCell" key={label}>
                <span className="branding__swatchBox" style={{background: inUse}}/>
                <b>{label}</b>
                <div className="branding__colorInputs">
                    <input type="color"
                           aria-label={`${label} color picker`}
                           value={/^#[0-9a-fA-F]{6}$/.test(inUse) ? inUse : "#000000"}
                           onChange={this.set(key)}/>
                    <input className="branding__hexInput"
                           aria-label={`${label} hex value`}
                           placeholder={calculated}
                           value={value}
                           onChange={this.set(key)}/>
                </div>
                {overridden
                    ? <button className="branding__link branding__reset"
                              onClick={() => this.resetColor(key)}>
                        Reset to {calculated}
                      </button>
                    : <em>calculated &middot; {note}</em>}
            </div>
        );
    }

    private removeLogo = () => {
        // Drops a not-yet-uploaded file outright; nothing was stored, so there is nothing to clear.
        if (this.state.pendingLogo) {
            this.clearPendingLogo();
            return;
        }
        // An empty string is the explicit "clear it" signal; the save that follows persists it.
        this.setState(prev => ({form: {...prev.form, logoUrl: ""}, brand: prev.brand
            ? {...prev.brand, logoUrl: ""} : null}));
    };

    componentDidUpdate(_: RouteComponentProps<Params>, prevState: State) {
        // Errors are shown at the top of a long form, out of sight of the Save button that
        // produced them, so a new one is scrolled into view and given focus, which also makes
        // screen readers announce it.
        const node = this.errorRef.current;
        if (!node || !this.state.error || this.state.error === prevState.error) return;
        const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        node.scrollIntoView({behavior: reduceMotion ? "auto" : "smooth", block: "center"});
        node.focus({preventScroll: true});
    }

    componentWillUnmount() {
        // Object URLs hold the file in memory until revoked.
        if (this.state.pendingLogoPreview) URL.revokeObjectURL(this.state.pendingLogoPreview);
    }

    private resetColor = (key: keyof BrandRequest) => {
        this.setState(prev => ({form: {...prev.form, [key]: ""}}), this.refreshTheme);
    };

}
